import type { EpisodeSummary, Me } from "@scrile/contracts";

/**
 * Состояния серий для списка и решения «что играть дальше». Доступ решает сервер
 * (entitlement); здесь только чтение его ответа: available, locked, releasedAt.
 */

export type EpisodeState = "free" | "available" | "locked" | "upcoming";

/**
 * - free — бесплатная и доступна (пилот);
 * - available — доступна по подписке или «всё сразу»;
 * - locked — вышла, но нужна подписка: тап ведёт на пейвол;
 * - upcoming — ещё не вышла. Важнее «locked»: гостю про серию, которой нет, рано
 *   говорить «заплати» — сначала она должна выйти.
 */
export function episodeState(episode: EpisodeSummary): EpisodeState {
  if (episode.available) return episode.free ? "free" : "available";
  if (episode.releasedAt === null) return "upcoming";
  return episode.locked ? "locked" : "upcoming";
}

export function isPlayable(episode: EpisodeSummary): boolean {
  return episode.available;
}

/** Следующая доступная серия после данной — для автоперехода. */
export function nextPlayable(episodes: readonly EpisodeSummary[], afterOrdinal: number): EpisodeSummary | null {
  return (
    [...episodes].sort((a, b) => a.ordinal - b.ordinal).find((e) => e.ordinal > afterOrdinal && e.available) ?? null
  );
}

/** Серия сразу за данной, в любом состоянии: по ней видно, нужен ли пейвол. */
export function followingEpisode(episodes: readonly EpisodeSummary[], ordinal: number): EpisodeSummary | null {
  return [...episodes].sort((a, b) => a.ordinal - b.ordinal).find((e) => e.ordinal > ordinal) ?? null;
}

/**
 * С чего продолжить: первая доступная серия, которую не дослушали; если всё доступное
 * прослушано — последняя доступная. finished — из памяти позиций этого устройства.
 */
export function resumeTarget(
  episodes: readonly EpisodeSummary[],
  finished: (ordinal: number) => boolean,
): EpisodeSummary | null {
  const playable = [...episodes].filter((e) => e.available).sort((a, b) => a.ordinal - b.ordinal);
  return playable.find((e) => !finished(e.ordinal)) ?? playable[playable.length - 1] ?? null;
}

/** Подписка даёт доступ (пробный период и просрочка платежа тоже — как у сервера). */
export function isSubscribed(me: Me | null): boolean {
  const status = me?.subscription?.status;
  const demo = me?.demoAccessUntil ? Date.parse(me.demoAccessUntil) > Date.now() : false;
  return status === "trialing" || status === "active" || status === "past_due" || demo;
}

/** Слушать сезон целиком можно: подписка или «всё сразу». */
export function hasAccess(me: Me | null): boolean {
  return isSubscribed(me) || me?.unlockedAll === true;
}
